import { createContext, useState, useMemo, useCallback } from 'react'
import { node } from 'prop-types'
import { api } from 'api'

interface Template {
  id: string
  name: string
  folder?: { id: string; name: string }
  fieldsets?: { id: string; name: string }[]
  patterns?: { field: { id: string; name: string } }[]
  rollup_dataset_id?: string
  created_at: string
  settings?: {
    require_approval?: boolean
  }
}

interface TemplateContextType {
  byId: Record<string, Template>
  fetchTemplates: () => Promise<Template[]>
  fetchTemplate: (id: string) => Promise<Template>
}

export const templateContext = createContext<TemplateContextType>({
  byId: {},
  fetchTemplates: () => Promise.resolve([]),
  fetchTemplate: () => Promise.resolve({} as Template),
})

export const TemplateProvider = ({ children }) => {
  const [byId, setById] = useState({})
  const fetchTemplates = useCallback(
    () =>
      api
        .get<
          Template[]
        >('/api/v1/account/template?with[]=folder:id,name&with[]=fieldsets:id,name&with[]=patterns:field.id,name')
        .then(({ data }) => {
          setById(prev => data.reduce((acc, row) => ({ ...acc, [row.id]: row }), prev))
          return data
        }),
    [],
  )

  const fetchTemplate = useCallback(
    (id: string) =>
      api
        .get<Template>(
          `/api/v1/account/template/${id}?with[]=folder:id,name&with[]=fieldsets:id,name&with[]=patterns:field.id,name`,
        )
        .then(({ data }) => {
          setById(prev => ({ ...prev, [data.id]: data }))
          return data
        }),
    [],
  )

  const importBulkUploadTemplate = useCallback(
    (templateId, formData) => api.post(`/api/v1/account/template/${templateId}/import`, formData),
    [],
  )

  const updateTemplates = newTemplates => {
    setById(newTemplates)
  }

  const value = useMemo(
    () => ({
      byId,
      fetchTemplates,
      fetchTemplate,
      importBulkUploadTemplate,
      setById,
      updateTemplates,
    }),
    [byId, fetchTemplates, fetchTemplate, importBulkUploadTemplate, setById, updateTemplates],
  )

  return <templateContext.Provider value={value}>{children}</templateContext.Provider>
}

TemplateProvider.propTypes = {
  children: node.isRequired,
}
